import { Stack, Typography } from '@mui/material';
import { ClosedQuestion, EffectQuestion, OpenQuestion } from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import ParticipantList from '../participants/participant-list';
import LeadQuestionsCreation from './lead-questions-creation';

interface Props {
  newName: string;
  expanded: string[];
  closedQuestions: ClosedQuestion[];
  selectedClosedQuestions: string[];
  openQuestions: OpenQuestion[];
  selectedOpenQuestions: string[];
  effectQuestions: EffectQuestion[];
  selectedEffectQuestions: string[];
  participantsToAdd: Array<{ name: string; perspective: string }>;

  handleSaveTag(): void;

  handleToggleTreeNode(event: React.ChangeEvent<any>, nodeIds: string[]): void;

  handleSelectionClosedQuestionsChange(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSelectionOpenQuestionsChange(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSelectionEffectQuestionsChange(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleOpenAddQuestionsDialog(subCategoryId: string, categoryId?: string, categorySourceId?: string, subCategorySourceId?: string): () => void;

  handleOpenAddParticipantsDialog(): void;

  getRealPerspective(id: string): string;

  handleOpenAddNewClosedQuestionDialog(): void;

  handleOpenAddNewEffectQuestionsDialog(): void;

  handleOpenAddNewOpenQuestionsDialog(): void;

  handleOpenConfirmDeleteLeadQuestionDialog(questionId: string): () => void;

  handleOpenConfirmDeleteLeadCategoryDialog(categoryId: string): (event: React.MouseEvent) => void;

  handleOpenAddDimensionDialog(categoryId?: string): (event: React.MouseEvent) => void;

  moveClosedCategory(type: 'down' | 'up', categoryId: string): (event: React.MouseEvent) => void;

  moveCompetence(type: 'down' | 'up', categoryId: string, subCategoryId: string): (event: React.MouseEvent) => void;

  moveClosedQuestion(type: 'down' | 'up', categoryId: string, subCategoryId: string, questionId: string): (event: React.MouseEvent) => void;

  moveEffectCategory(type: 'down' | 'up', categoryId: string): (event: React.MouseEvent) => void;

  moveEffectQuestion(type: 'down' | 'up', subCategoryId: string, questionId: string): (event: React.MouseEvent) => void;

  moveOpenQuestion(type: 'down' | 'up', questionId: string): (event: React.MouseEvent) => void;
}

const QuestionnaireCreationView: React.FC<Props> = ({
  newName,
  expanded,
  closedQuestions,
  selectedClosedQuestions,
  openQuestions,
  selectedOpenQuestions,
  effectQuestions,
  selectedEffectQuestions,
  participantsToAdd,
  handleSaveTag,
  handleToggleTreeNode,
  handleSelectionClosedQuestionsChange,
  handleSelectionOpenQuestionsChange,
  handleSelectionEffectQuestionsChange,
  handleOpenAddQuestionsDialog,
  handleOpenAddParticipantsDialog,
  getRealPerspective,
  handleOpenAddNewClosedQuestionDialog,
  handleOpenAddNewEffectQuestionsDialog,
  handleOpenAddNewOpenQuestionsDialog,
  handleOpenConfirmDeleteLeadQuestionDialog,
  handleOpenConfirmDeleteLeadCategoryDialog,
  handleOpenAddDimensionDialog,
  moveClosedCategory,
  moveCompetence,
  moveClosedQuestion,
  moveEffectCategory,
  moveEffectQuestion,
  moveOpenQuestion,
}) => {
  const { classes, cx } = useStyles();

  return (
    <>
      <Stack className={classes.topActions} direction={'row'} alignItems={'center'} gap={2}>
        <PaiLink text={'+ Kategorie'} variant={'primary'} underline={'always'} size={'md'} action={handleOpenAddNewClosedQuestionDialog} />
        <PaiLink text={'+ Effektive Führung'} variant={'primary'} underline={'always'} size={'md'} action={handleOpenAddNewEffectQuestionsDialog} />
        <PaiLink text={'+ Persönliche Eindrücke'} variant={'primary'} underline={'always'} size={'md'} action={handleOpenAddNewOpenQuestionsDialog} />
        <PaiButton text={'Speichern'} size={'small'} action={handleSaveTag} style={{ marginLeft: '16px' }} />
      </Stack>
      <Stack className={classes.leadDetails} direction={{ xs: 'column', xl: 'row' }} gap={1}>
        <div className={cx(classes.content, 'large')}>
          <div className={classes.title}>
            <Typography className={classes.titleText}>Fragenkatalog von {newName || '...'}</Typography>
          </div>
          <LeadQuestionsCreation
            closedQuestions={closedQuestions}
            selectedClosedQuestions={selectedClosedQuestions}
            effectQuestions={effectQuestions}
            selectedEffectQuestions={selectedEffectQuestions}
            openQuestions={openQuestions}
            selectedOpenQuestions={selectedOpenQuestions}
            expanded={expanded}
            handleToggleTreeNode={handleToggleTreeNode}
            handleOpenAddDimensionDialog={handleOpenAddDimensionDialog}
            handleOpenConfirmDeleteLeadCategoryDialog={handleOpenConfirmDeleteLeadCategoryDialog}
            moveClosedCategory={moveClosedCategory}
            moveCompetence={moveCompetence}
            handleSelectionClosedQuestionsChange={handleSelectionClosedQuestionsChange}
            handleOpenConfirmDeleteLeadQuestionDialog={handleOpenConfirmDeleteLeadQuestionDialog}
            moveClosedQuestion={moveClosedQuestion}
            handleOpenAddQuestionsDialog={handleOpenAddQuestionsDialog}
            moveEffectCategory={moveEffectCategory}
            handleSelectionEffectQuestionsChange={handleSelectionEffectQuestionsChange}
            moveEffectQuestion={moveEffectQuestion}
            handleSelectionOpenQuestionsChange={handleSelectionOpenQuestionsChange}
            moveOpenQuestion={moveOpenQuestion}
            handleOpenAddNewOpenQuestionsDialog={handleOpenAddNewOpenQuestionsDialog}
          />
        </div>
        <div className={cx(classes.content, 'small')}>
          <div className={classes.title}>
            <Typography className={classes.titleText}>Feedbackgeber</Typography>
          </div>
          <ParticipantList
            participantsToAdd={participantsToAdd}
            isCreationView={true}
            handleOpenAddParticipantsDialog={handleOpenAddParticipantsDialog}
            getRealPerspective={getRealPerspective}
          />
        </div>
      </Stack>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  topActions: {
    position: 'absolute',
    right: '32px',
    top: 0,
    height: '60px',
    zIndex: 1300,
  },
  leadDetails: {
    padding: '8px',
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    maxHeight: 'calc(960px - 60px - 32px)',
    [theme.breakpoints.down('xl')]: {
      height: 'fit-content',
      maxHeight: 'unset',
    },
  },
  content: {
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '12px 16px',
    height: '100%',
    '&.small': {
      flexBasis: '33%',
    },
    '&.large': {
      flexBasis: '67%',
    },
    [theme.breakpoints.down('xl')]: {
      flexBasis: '100%',
    },
  },
  title: {
    marginBottom: '32px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
  },

  treeItem: {
    '& .MuiTreeItem-content:hover .arrows': {
      visibility: 'visible',
    },
  },
  question: {
    '& .MuiTypography-body1': {
      width: '100%',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:hover .arrows': {
      visibility: 'visible',
    },
  },
  arrows: {
    visibility: 'hidden',
  },
}));

export default QuestionnaireCreationView;
